import createMuiTheme, { ThemeOptions } from '@material-ui/core/styles/createMuiTheme';
import { PaletteOptions } from '@material-ui/core/styles/createPalette';
import { Overrides } from '@material-ui/core/styles/overrides'; // eslint-disable-line import/no-unresolved
import { defaultsDeep } from 'lodash';
import { getMuiTheme } from 'material-ui/styles';

import getPropsLuma from './getPropsLuma';
import getPropsLumaDark from './getPropsLumaDark';
import getPropsLumaLegacy from './getPropsLumaLegacy';
import getPropsLumaOld from './getPropsLumaOld';

const fontFamily = 'halyard-text, sans-serif';
const headersFontFamily = 'halyard-display, sans-serif';

const pink = {
  500: '#E5A4CB',
};
const colorAccentLotus = pink[500];

const yellow = {
  500: '#FFE54D',
};
const colorLightYellow = yellow[500];

const orange = {
  200: '#FFF3EB',
  300: '#FFE1A6',
  400: '#FFD17A',
  500: '#FFC24D',
  800: '#FF6600',
};
const colorAccentLuma = orange[800];
const accent1Color = orange[800];
const accent2Color = orange[500];
const colorAccentPeach = orange[400];
const colorAccentSunrise = orange[300];
const colorAccentPeachLight = orange[200];

const purple = {
  900: '#14143D',
  800: '#313199',
  700: '#5A5AAD',
  500: '#A4B2E5',
  300: '#DFDFF9',
  100: '#F5F5FF',
};
const colorSupportMidnight = purple[900];
const primary1Color = purple[800];
const colorSupportSlate = purple[800];
const colorSupportSteel = purple[700];
const colorAccentLavander = purple[500];
const colorSupportPowder = purple[300];
const colorLightBlue = purple[300];
const colorSupportSnow = purple[100];

const teal = {
  700: '#3A719B',
  500: '#2595A5',
  300: '#92D9D5',
};

const colorDarkModerateBlue = teal[700];
const colorDarkCyan = teal[500];
const colorSupportAqua = teal[300];

const blue = {
  800: '#507BE9',
  700: '#4384f5',
  300: '#A6DFFF',
};
const colorAccentTeal = blue[800];
const colorMessageBubble = blue[700];
const colorAccentSky = blue[300];

const red = {
  700: '#993131',
  600: '#A30000',
  500: '#CC0000',
  400: '#E68080',
  100: '#F5EAEA',
};
const colorDarkPink = red[700];
const colorDangerHover = red[600];
const colorDanger = red[500];
const colorAccentWatermelon = red[400];
const colorLightGrayishPink = red[100];

const green = {
  500: '#277A35',
  400: '#319942',
  100: '#EAF5EC',
};
const colorSuccessHover = green[500];
const colorSuccess = green[400];
const colorAccentAloe = green[400];
const colorSuccessBackground = green[100];

const gray = {
  700: '#666',
  600: '#808080',
  500: '#C5C5C5',
  400: '#D9D9D9',
  300: '#E2E2E2',
  200: '#E9EFF0',
  100: '#F2F2F2',
};
const colorDarkGray = gray[600];
const colorGrayishBlue = gray[500];
const colorMostlyDesaturatedDarkBlue = gray[400];
const colorLightGrayishBlue = gray[300];
const colorSupportGray = gray[200];
const colorVeryLightGray = gray[100];

const black = '#000';
const colorInfo = black;
const textColor = black;

const palette = {
  // this items before "custom" is only needed to deprecated muiTheme
  type: 'light',
  primary1Color,
  accent1Color,
  accent2Color,
  textColor,
  custom: {
    type: 'light',
    primary1Color,
    accent1Color,
    accent2Color,
    textColor,

    colorSuccess,
    colorSuccessHover,
    colorDanger,
    colorDangerHover,
    colorInfo,
    colorSuccessBackground,
    colorGrayishBlue,
    colorLightGrayishBlue,
    colorMostlyDesaturatedDarkBlue,
    colorDarkGray,
    colorDarkPink,
    colorLightGrayishPink,
    colorDarkModerateBlue,
    colorLightBlue,
    colorDarkCyan,
    colorVeryLightGray,
    colorSupportGray,
    colorSupportSlate,
    colorSupportSnow,
    colorLightYellow,
    colorAccentAloe,
    colorAccentLavander,
    colorAccentLotus,
    colorAccentLuma,
    colorAccentPeach,
    colorAccentSky,
    colorAccentSunrise,
    colorAccentPeachLight,
    colorAccentWatermelon,
    colorSupportAqua,
    colorSupportMidnight,
    colorSupportPowder,
    colorSupportSteel,
    colorAccentTeal,
    colorMessageBubble,
  },
};

interface TLumaPaletteOptions extends PaletteOptions {
  tertiary: {
    main: string;
  };
}

interface TLumaOverrides extends Overrides {
  LumaIconButton: {
    colorPrimary: {
      color: string;
    };
  };
}

interface TLumaThemeOptions extends ThemeOptions {
  palette: TLumaPaletteOptions;
  overrides: TLumaOverrides;
  custom: {
    headersFontFamily: string;
    fontSizes: {
      title: number;
      largeIcon: number;
      heading: number;
      subHeading: number;
      bodyG: number;
      body: number;
      bodyS: number;
      small: number;
      tiny: number;
    };
    palette: typeof palette.custom;
    MuiInput: {
      disableUnderline: boolean;
    };
    MuiDrawer: {
      width: number;
    };
  };
}

export const defaultProps: TLumaThemeOptions = {
  typography: {
    useNextVariants: true,
    fontFamily,
  },
  palette: {
    background: {
      default: purple[100],
    },
    primary: {
      main: purple[800],
    },
    secondary: {
      main: orange[800],
    },
    tertiary: {
      main: '#fff',
    },
    action: {
      hover: gray[300],
    },
    text: {
      primary: black,
      secondary: gray[700],
    },
  },
  overrides: {
    MuiInput: {
      input: {
        '&::placeholder': {
          color: purple[700],
          fontSize: 14,
          opacity: 1,
        },
        '&::-ms-clear': {
          display: 'none',
        },
        '& ::-ms-clear': {
          display: 'none',
        },
        '&::-ms-reveal': {
          display: 'none',
        },
        '& ::-ms-reveal': {
          display: 'none',
        },
        '&:invalid': {
          boxShadow: 'none',
        },
        '&:-moz-submit-invalid': {
          boxShadow: 'none',
        },
        '&:-moz-ui-invalid': {
          boxShadow: 'none',
        },
        '& :invalid': {
          boxShadow: 'none',
        },
        '& :-moz-submit-invalid': {
          boxShadow: 'none',
        },
        '& :-moz-ui-invalid': {
          boxShadow: 'none',
        },
      },
    },
    // https://app.clickup.com/t/fb5gq
    MuiIconButton: {
      colorPrimary: {
        color: purple[800],
      },
    },
    MuiFormControlLabel: {
      label: {
        color: black,
      },
    },
    MuiFormHelperText: {
      root: {
        cursor: 'default',
      },
    },
    MuiIcon: {
      colorPrimary: {
        color: purple[800],
      },
      fontSizeSmall: {
        fontSize: 18,
      },
      fontSizeLarge: {
        fontSize: 32,
      },
    },
    MuiSvgIcon: {
      colorPrimary: {
        color: purple[800],
      },
      fontSizeSmall: {
        fontSize: 18,
      },
      fontSizeLarge: {
        fontSize: 32,
      },
    },
    MuiButton: {
      root: {
        color: black,
      },
      sizeLarge: {
        fontSize: 15,
      },
    },
    MuiDialogActions: {
      root: {
        padding: 16,
        margin: 0,
        background: gray[100],
      },
    },
    MuiFormLabel: {
      asterisk: {
        color: red[500],
      },
    },
    // WORKAROUND reverging primary color for icons changed on https://app.clickup.com/t/fb5gq
    LumaIconButton: {
      colorPrimary: {
        color: black,
      },
    },
  },
  custom: {
    headersFontFamily,
    fontSizes: {
      title: 40,
      largeIcon: 30,
      heading: 22,
      subHeading: 18,
      bodyG: 16,
      body: 15,
      bodyS: 14,
      small: 12,
      tiny: 10,
    },
    palette: palette.custom,
    // TODO: use props section
    // https://material-ui.com/customization/themes/#properties
    MuiInput: {
      disableUnderline: false,
    },
    MuiDrawer: {
      width: 216,
    },
  },
};

/**
 * DEPRECATED: Themes for material-ui alpha version
 */
const deprecatedMuiTheme = getMuiTheme({
  fontFamily,
  palette,
  toolbar: {
    backgroundColor: 'transparent',
  },
  toggle: {
    thumbOffColor: gray[100],
  },
});

const theme = createMuiTheme(defaultProps);
export const themeOptionsLuma = getPropsLuma(theme, defaultProps);
const muiThemeLuma = createMuiTheme(themeOptionsLuma);
const muiOldThemeLuma = createMuiTheme(getPropsLumaOld(theme));
const muiThemeLumaDark = createMuiTheme(getPropsLumaDark(theme, getPropsLuma(theme, defaultProps)));
const muiThemeLumaLegacy = createMuiTheme(getPropsLumaLegacy(theme, defaultProps));
const makeMuiCustomStyles = (customTheme: any) =>
  createMuiTheme(defaultsDeep(customTheme, getPropsLuma(theme, defaultProps)));

export { deprecatedMuiTheme, muiThemeLuma, muiOldThemeLuma, muiThemeLumaDark, muiThemeLumaLegacy, makeMuiCustomStyles };
